import type { AnalysisResult } from "./types";

const normalized = (value: string) => value.trim().toLocaleLowerCase("de-DE");

const demos: Record<string, AnalysisResult> = {
  mädchen: {
    query: "Mädchen",
    displayedTerm: "Mädchen",
    lemma: "Mädchen",
    partOfSpeech: "noun",
    translation: "ein weibliches Kind oder eine junge Person",
    shortMeaning: "Mädchen ist ein neutrales Nomen, obwohl es eine weibliche Person bezeichnet.",
    confidence: "high",
    gender: "neuter",
    article: "das",
    plural: "die Mädchen",
    nounCaseHint: "Singular und Plural haben dieselbe Wortform.",
    grammarNotes: [
      "Die Endung -chen macht Nomen fast immer neutral.",
      "Im Wörterbuch steht das Wort mit Artikel: das Mädchen."
    ],
    examples: [
      { de: "Das Mädchen liest ein Buch.", explanation: "Ein einfaches Beispiel im Singular." },
      { de: "Ich kenne das Mädchen aus Berlin.", explanation: "Hier steht das Nomen im Akkusativ." },
      { de: "Die Mädchen spielen im Hof.", explanation: "Die Pluralform sieht gleich aus." }
    ],
    related: ["das Kind", "die Frau", "jung"],
    source: "demo"
  },
  lief: {
    query: "lief",
    displayedTerm: "laufen",
    lemma: "laufen",
    partOfSpeech: "verb",
    translation: "sich zu Fuß bewegen; auch: funktionieren",
    shortMeaning: "lief ist die Präteritum-Form von laufen.",
    confidence: "high",
    verbForms: {
      infinitive: "laufen",
      present3rdPerson: "er/sie/es läuft",
      preterite: "lief",
      perfect: "ist gelaufen",
      imperative: "lauf!"
    },
    grammarNotes: [
      "laufen ist ein starkes Verb: Der Stammvokal verändert sich.",
      "Bei Bewegung bildet man das Perfekt meistens mit sein: ist gelaufen."
    ],
    examples: [
      { de: "Er lief gestern zehn Kilometer.", explanation: "Präteritum für eine Handlung in der Vergangenheit." },
      { de: "Die App läuft sehr schnell.", explanation: "Übertragene Bedeutung: funktionieren." },
      { de: "Wir sind nach Hause gelaufen.", explanation: "Perfekt mit sein bei Bewegung." }
    ],
    related: ["gehen", "rennen", "der Lauf"],
    source: "demo"
  },
  haus: {
    query: "дом",
    displayedTerm: "Haus",
    lemma: "Haus",
    partOfSpeech: "noun",
    translation: "ein Gebäude, in dem Menschen wohnen oder arbeiten",
    shortMeaning: "Haus ist die deutsche Entsprechung für russisch дом oder englisch house.",
    confidence: "high",
    gender: "neuter",
    article: "das",
    plural: "die Häuser",
    nounCaseHint: "Im Dativ Singular heißt es oft: in dem Haus oder im Haus.",
    grammarNotes: [
      "Die Grundform ist das Haus.",
      "Im Plural bekommt das Wort einen Umlaut: Haus -> Häuser."
    ],
    examples: [
      { de: "Das Haus ist alt.", explanation: "Ein kurzer Satz mit der Grundform." },
      { de: "Wir sind im Haus.", explanation: "im ist die Kurzform von in dem." },
      { de: "Viele Häuser haben einen Garten.", explanation: "Plural mit Umlaut: Häuser." }
    ],
    related: ["die Wohnung", "zu Hause", "bauen"],
    source: "demo"
  },
  häuser: {
    query: "Hauser",
    displayedTerm: "Haus",
    correctedQuery: "Häuser",
    correctionNote: "Wahrscheinlich fehlt der Umlaut: Hauser -> Häuser.",
    lemma: "Haus",
    partOfSpeech: "noun",
    translation: "ein Gebäude, in dem Menschen wohnen oder arbeiten",
    shortMeaning: "Häuser ist der Plural von das Haus.",
    confidence: "high",
    gender: "neuter",
    article: "das",
    plural: "die Häuser",
    nounCaseHint: "Im Plural steht der Artikel die: die Häuser.",
    grammarNotes: [
      "Die Wörterbuchform ist das Haus.",
      "Im Plural bekommt das Wort einen Umlaut: Haus -> Häuser."
    ],
    examples: [
      { de: "Das Haus ist alt.", explanation: "Singular mit dem Artikel das." },
      { de: "In dieser Straße stehen viele Häuser.", explanation: "Pluralform mit Umlaut." },
      { de: "Wir gehen nach Hause.", explanation: "nach Hause ist eine feste Wendung." }
    ],
    related: ["die Wohnung", "zu Hause", "bauen"],
    source: "demo"
  }
};

export function createDemoResult(query: string): AnalysisResult {
  const lookup = normalized(query);
  const key = lookup
    .replace("hauser", "häuser")
    .replace("дом", "haus")
    .replace("house", "haus")
    .replace("home", "haus");
  const direct = demos[key] ?? demos[normalized(query)] ?? demos.mädchen;

  return {
    ...direct,
    query,
    source: "demo"
  };
}
